import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import CollegeCard from '../components/CollegeCard';
import { STREAMS } from '../data/mock';
import { fetchColleges } from '../lib/api';

const TYPES = ['Public', 'Private (Deemed)', 'Autonomous', 'Central University', 'State'];

const Colleges = () => {
  const [params] = useSearchParams();
  const initialQ = params.get('q') || '';
  const [q, setQ] = useState(initialQ);
  const [selStreams, setSelStreams] = useState([]);
  const [selTypes, setSelTypes] = useState([]);
  const [sort, setSort] = useState('rank');
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchColleges().then((d) => setColleges(d)).catch(() => setColleges([])).finally(() => setLoading(false));
  }, []);

  const toggle = (arr, setter, v) =>
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const filtered = useMemo(() => {
    let list = colleges.filter((c) => {
      const matchesQ = !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.city || '').toLowerCase().includes(q.toLowerCase());
      const matchesStream = selStreams.length === 0 || (c.streams || []).some((s) => selStreams.includes(s));
      const matchesType = selTypes.length === 0 || selTypes.includes(c.type);
      return matchesQ && matchesStream && matchesType;
    });
    if (sort === 'rank') list = [...list].sort((a, b) => a.ranking - b.ranking);
    if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    if (sort === 'reviews') list = [...list].sort((a, b) => b.reviews - a.reviews);
    return list;
  }, [q, selStreams, selTypes, sort, colleges]);

  const clear = () => { setSelStreams([]); setSelTypes([]); setQ(''); };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Colleges in India</h1>
          <p className="text-slate-600 mt-1">Browse and compare top colleges by stream, ranking and fees.</p>

          <div className="mt-6 relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by college name or city..."
              className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm bg-white"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Filters */}
        <aside className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-teal-600" />
                <h3 className="font-semibold text-slate-900">Filters</h3>
              </div>
              {(selStreams.length + selTypes.length + (q ? 1 : 0)) > 0 && (
                <button onClick={clear} className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>

            <div className="mb-5">
              <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Stream</div>
              <div className="space-y-1.5">
                {STREAMS.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-teal-700">
                    <input type="checkbox" checked={selStreams.includes(s.key)} onChange={() => toggle(selStreams, setSelStreams, s.key)} className="accent-teal-600" />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 mb-2">College Type</div>
              <div className="space-y-1.5">
                {TYPES.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-teal-700">
                    <input type="checkbox" checked={selTypes.includes(t)} onChange={() => toggle(selTypes, setSelTypes, t)} className="accent-teal-600" />
                    {t}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <div className="text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-900">{filtered.length}</span> of {colleges.length} colleges
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Sort by:</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:border-teal-500">
                <option value="rank">Ranking</option>
                <option value="rating">Rating</option>
                <option value="reviews">Most Reviewed</option>
              </select>
            </div>
          </div>

          {(selStreams.length + selTypes.length) > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {selStreams.map((s) => (
                <Badge key={s} className="bg-teal-100 text-teal-700 hover:bg-teal-100 cursor-pointer" onClick={() => toggle(selStreams, setSelStreams, s)}>
                  {STREAMS.find((x) => x.key === s)?.label} <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
              {selTypes.map((t) => (
                <Badge key={t} className="bg-orange-100 text-orange-700 hover:bg-orange-100 cursor-pointer" onClick={() => toggle(selTypes, setSelTypes, t)}>
                  {t} <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
              <p className="text-slate-600">No colleges match your filters.</p>
              <Button onClick={clear} className="mt-4 bg-teal-600 hover:bg-teal-700 text-white">Reset filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((c) => <CollegeCard key={c.id} college={c} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Colleges;
